<template>
  <div class="d-flex flex-column flex-grow-1 rounded p-3">
    <BaseToolBar
      title="Indicateurs sinistres"
      subtitle="Statistiques et données sur les sinistres des véhicules"
    >
      <div class="btn-group mr-1" role="group">
        <BaseButton
          class="btn btn-primary ml-3"
          v-tooltip="'Exporter les statistiques'"
          @click="exportStats"
          :loading="loading"
          text="Exporter (.xls)"
        ></BaseButton>
      </div>
      <router-link to="/vehicules" class="btn btn-secondary">
        Retour à la liste
      </router-link>
    </BaseToolBar>

    <!-- Loading indicator -->
    <div v-if="loading" class="d-flex justify-content-center my-5">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Chargement...</span>
      </div>
    </div>

    <div v-else class="row mt-3">
      <!-- Cartes de statistiques générales -->
      <div class="col-md-3 mb-4">
        <div class="card h-100 border-primary">
          <div class="card-body text-center">
            <h5 class="card-title text-primary">Total des sinistres</h5>
            <div class="display-4">{{ sinistres.length }}</div>
          </div>
        </div>
      </div>
      <div class="col-md-3 mb-4">
        <div class="card h-100 border-warning">
          <div class="card-body text-center">
            <h5 class="card-title text-warning">Coût total société</h5>
            <div class="display-4">{{ formatCurrency(coutTotalSociete) }}</div>
          </div>
        </div>
      </div>
      <div class="col-md-3 mb-4">
        <div class="card h-100 border-info">
          <div class="card-body text-center">
            <h5 class="card-title text-info">Coût total assurance</h5>
            <div class="display-4">
              {{ formatCurrency(coutTotalAssurance) }}
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-3 mb-4">
        <div class="card h-100 border-danger">
          <div class="card-body text-center">
            <h5 class="card-title text-danger">Coût moyen par sinistre</h5>
            <div class="display-4">{{ formatCurrency(coutMoyenSinistre) }}</div>
          </div>
        </div>
      </div>

      <!-- Blocs d'état cliquables -->
      <div class="col-12 mb-4">
        <div class="card">
          <div class="card-header bg-dark text-white">
            <h5 class="mb-0">État des sinistres</h5>
          </div>
          <div class="card-body">
            <div class="row">
              <!-- Réduire la largeur de chaque carte de col-md-3 (4 cartes) à col-md-2.4 (5 cartes) -->
              <div
                class="col-md-2-4 mb-2"
                v-for="(etat, index) in etats"
                :key="index"
              >
                <div
                  class="card card-etat h-100"
                  :class="{
                    'card-etat-active': filtreEtat == etat.id.toString()
                  }"
                  @click="setFiltreEtat(etat.id.toString())"
                >
                  <div
                    class="card-body text-center"
                    :class="`bg-${getEtatClass(etat.id)}-light`"
                  >
                    <h6
                      class="card-title"
                      :class="`text-${getEtatClass(etat.id)}`"
                    >
                      {{ etat.description }}
                    </h6>
                    <div
                      class="display-4"
                      :class="`text-${getEtatClass(etat.id)}`"
                    >
                      {{ countSinistresByEtat(etat.id) }}
                    </div>
                    <div class="small mt-2">
                      {{ formatCurrency(totalCoutByEtat(etat.id)) }}
                    </div>
                  </div>
                </div>
              </div>
              <!-- La carte "Tous les sinistres" avec la même largeur -->
              <div class="col-md-2-4 mb-2">
                <div
                  class="card card-etat h-100"
                  :class="{ 'card-etat-active': filtreEtat === '-1' }"
                  @click="setFiltreEtat('-1')"
                >
                  <div class="card-body text-center bg-light">
                    <h6 class="card-title text-dark">Tous les sinistres</h6>
                    <div class="display-4 text-dark">
                      {{ sinistres.length }}
                    </div>
                    <div class="small mt-2">
                      {{
                        formatCurrency(coutTotalSociete + coutTotalAssurance)
                      }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 10 derniers sinistres avec contrôle de pagination -->
      <div class="col-md-12 mb-4">
        <div class="card h-100">
          <div
            class="card-header bg-secondary text-white d-flex justify-content-between align-items-center"
          >
            <h5 class="mb-0">
              Liste des sinistres
              {{ filtreEtatLabel ? `- ${filtreEtatLabel}` : "" }}
            </h5>
            <div class="d-flex">
              <select
                v-model="filtreEtat"
                class="form-control form-control-sm mr-2"
              >
                <option value="-1">Tous les états</option>
                <option value="0">Déclaration en cours</option>
                <option value="1">En expertise</option>
                <option value="2">En réparation</option>
                <option value="3">Traité</option>
              </select>
              <select
                v-model="filtreType"
                class="form-control form-control-sm mr-2"
              >
                <option value="-1">Tous les types</option>
                <option value="0">Carrosserie</option>
                <option value="1">Mécanique</option>
                <option value="2">Bris de glace</option>
                <option value="3">Vol</option>
                <option value="4">Incendie</option>
                <option value="5">Autre</option>
              </select>
              <select
                v-model="limiteSinistres"
                class="form-control form-control-sm"
              >
                <option :value="10">10 sinistres</option>
                <option :value="20">20 sinistres</option>
                <option :value="50">50 sinistres</option>
                <option :value="100">100 sinistres</option>
              </select>
            </div>
          </div>
          <div class="card-body">
            <table class="table table-striped table-hover">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Véhicule</th>
                  <th>Collaborateur</th>
                  <th>Type</th>
                  <th>État</th>
                  <th>Coût total</th>
                  <th class="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="sinistre in derniersSinistresFiltres"
                  :key="sinistre.id"
                >
                  <td>{{ formatDate(sinistre.date_sinistre) }}</td>
                  <td>
                    <span class="badge badge-light">{{
                      sinistre.immatriculation || "N/A"
                    }}</span>
                    {{ sinistre.modele || "Non défini" }}
                  </td>
                  <td>
                    <span
                      v-if="
                        sinistre.collaborateur_nom ||
                          sinistre.collaborateur_prenom
                      "
                    >
                      {{
                        `${sinistre.collaborateur_nom ||
                          ""} ${sinistre.collaborateur_prenom || ""}`.trim()
                      }}
                    </span>
                    <span v-else class="text-muted">Non attribué</span>
                  </td>
                  <td>
                    <span
                      class="badge"
                      :class="getTypeDommageBadgeClass(sinistre.type_dommage)"
                    >
                      {{ getTypeDommageLabel(sinistre.type_dommage) }}
                    </span>
                  </td>
                  <td>
                    <span
                      class="badge"
                      :class="getEtatBadgeClass(sinistre.etat)"
                    >
                      {{ getEtatLabel(sinistre.etat) }}
                    </span>
                  </td>
                  <td class="font-weight-bold">
                    {{ formatCurrency(getSinistreCoutTotal(sinistre)) }}
                  </td>
                  <td class="text-center">
                    <router-link
                      :to="
                        `/vehicules/detail/${sinistre.vehicule_id}?tab=sinistres`
                      "
                      class="btn btn-sm btn-outline-primary"
                    >
                      <i class="fas fa-eye mr-1"></i> Voir
                    </router-link>
                  </td>
                </tr>
                <tr v-if="derniersSinistresFiltres.length === 0">
                  <td colspan="7" class="text-center">
                    Aucun sinistre ne correspond aux critères sélectionnés
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- Pagination simple -->
            <div class="d-flex justify-content-between align-items-center mt-3">
              <div>
                Affichage de {{ derniersSinistresFiltres.length }} sur
                {{ sinistresFiltres.length }} sinistres
              </div>
              <div>
                <button
                  class="btn btn-sm btn-outline-secondary"
                  @click="voirTousSinistres"
                  v-if="sinistresFiltres.length > limiteSinistres"
                >
                  Voir tous les sinistres ({{ sinistresFiltres.length }})
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Nouvelles cartes de statistiques -->
      <div class="col-md-4 mb-4">
        <div class="card h-100 border-success">
          <div class="card-body">
            <h5 class="card-title text-success">État des sinistres</h5>
            <div class="progress-stacked mb-3">
              <div
                v-for="(etat, index) in etatsStatistiques"
                :key="index"
                class="progress"
                :class="`bg-${getEtatClass(index)}`"
                :style="`width: ${etat.pourcentage}%`"
                role="progressbar"
                :aria-valuenow="etat.pourcentage"
                aria-valuemin="0"
                aria-valuemax="100"
                :title="`${etat.label}: ${etat.count} (${etat.pourcentage}%)`"
              ></div>
            </div>
            <div class="d-flex flex-wrap mt-3">
              <div
                v-for="(etat, index) in etatsStatistiques"
                :key="index"
                class="mr-3 mb-2"
              >
                <span class="badge" :class="`badge-${getEtatClass(index)}`">
                  {{ etat.count }}
                </span>
                {{ etat.label }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-4 mb-4">
        <div class="card h-100 border-info">
          <div class="card-body">
            <h5 class="card-title text-info">
              Répartition par type de dommage
            </h5>
            <div v-if="!hasTypeDommageData" class="text-center text-muted py-4">
              Aucune donnée disponible
            </div>
            <apexchart
              v-else
              type="donut"
              height="180"
              :options="typeDommageChartOptions"
              :series="typeDommageSeries"
            ></apexchart>
          </div>
        </div>
      </div>

      <div class="col-md-4 mb-4">
        <div class="card h-100 border-warning">
          <div class="card-body">
            <h5 class="card-title text-warning">
              Répartition par responsabilité
            </h5>
            <div
              v-if="!hasResponsabiliteData"
              class="text-center text-muted py-4"
            >
              Aucune donnée disponible
            </div>
            <apexchart
              v-else
              type="donut"
              height="180"
              :options="responsabiliteChartOptions"
              :series="responsabiliteSeries"
            ></apexchart>
          </div>
        </div>
      </div>

      <!-- Graphique évolution mensuelle -->
      <div class="col-md-12 mb-4">
        <div class="card h-100">
          <div class="card-header bg-dark text-white">
            <h5 class="mb-0">Évolution mensuelle des sinistres</h5>
          </div>
          <div class="card-body">
            <div
              class="chart-container"
              style="position: relative; height:300px;"
            >
              <apexchart
                type="line"
                height="300"
                :options="monthlyChartOptions"
                :series="monthlySeries"
              ></apexchart>
            </div>
          </div>
        </div>
      </div>

      <!-- Remplacer le graphique de répartition des coûts par circonstances de dommages -->
      <div class="col-md-6 mb-4">
        <div class="card h-100">
          <div class="card-header bg-primary text-white">
            <h5 class="mb-0">Circonstances de dommages</h5>
          </div>
          <div class="card-body">
            <div
              v-if="!hasCirconstanceData"
              class="text-center text-muted py-5"
            >
              Aucune donnée disponible
            </div>
            <apexchart
              v-else
              type="bar"
              height="300"
              :options="circonstanceChartOptions"
              :series="circonstanceSeries"
            ></apexchart>
          </div>
        </div>
      </div>

      <!-- Répartition trimestrielle -->
      <div class="col-md-6 mb-4">
        <div class="card h-100">
          <div class="card-header bg-info text-white">
            <h5 class="mb-0">Répartition trimestrielle</h5>
          </div>
          <div class="card-body">
            <apexchart
              type="bar"
              height="300"
              :options="quarterlyChartOptions"
              :series="quarterlySeries"
            ></apexchart>
          </div>
        </div>
      </div>

      <!-- Top 10 des véhicules avec le plus de sinistres -->
      <div class="col-md-12 mb-4">
        <div class="card h-100">
          <div class="card-header bg-warning text-dark">
            <h5 class="mb-0">
              Top 10 des véhicules avec le plus de sinistres
            </h5>
          </div>
          <div class="card-body">
            <table class="table table-striped">
              <thead>
                <tr>
                  <th>Véhicule</th>
                  <th>Immatriculation</th>
                  <th class="text-center">Nb sinistres</th>
                  <th class="text-center">Coût société</th>
                  <th class="text-center">Coût assurance</th>
                  <th class="text-center">Coût total</th>
                  <th class="text-center">Détails</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="vehicule in topVehiculesSinistres"
                  :key="vehicule.immatriculation"
                >
                  <td>{{ vehicule.modele || "Non défini" }}</td>
                  <td>{{ vehicule.immatriculation || "Non défini" }}</td>
                  <td class="text-center">
                    <span class="badge badge-warning">
                      {{ vehicule.nbSinistres }}
                    </span>
                  </td>
                  <td class="text-center">
                    {{ formatCurrency(vehicule.coutSociete) }}
                  </td>
                  <td class="text-center">
                    {{ formatCurrency(vehicule.coutAssurance) }}
                  </td>
                  <td class="text-center font-weight-bold">
                    {{ formatCurrency(vehicule.coutTotal) }}
                  </td>
                  <td class="text-center">
                    <router-link
                      :to="
                        '/vehicules/detail/' +
                          vehicule.vehicule_id +
                          '?tab=sinistres'
                      "
                      class="btn btn-sm btn-outline-info"
                    >
                      Détails
                    </router-link>
                  </td>
                </tr>
                <tr v-if="topVehiculesSinistres.length === 0">
                  <td colspan="7" class="text-center">
                    Aucune donnée disponible
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Top collaborateurs impliqués dans des sinistres -->
      <div class="col-md-6 mb-4">
        <div class="card h-100">
          <div class="card-header bg-danger text-white">
            <h5 class="mb-0">
              Top 5 collaborateurs impliqués
            </h5>
          </div>
          <div class="card-body">
            <table class="table table-striped">
              <thead>
                <tr>
                  <th>Collaborateur</th>
                  <th class="text-center">Nombre sinistres</th>
                  <th class="text-center">Responsabilité</th>
                  <th class="text-center">Coût total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(collab, index) in topCollaborateurs" :key="index">
                  <td>
                    <span v-if="collab.nom">{{ collab.nom }}</span>
                    <span v-else class="text-muted">Non attribué</span>
                  </td>
                  <td class="text-center">
                    <span class="badge badge-danger">{{ collab.count }}</span>
                  </td>
                  <td class="text-center">
                    <span v-if="collab.responsabiliteScore !== undefined">
                      {{
                        getResponsabilitePourcentage(collab.responsabiliteScore)
                      }}
                    </span>
                    <span v-else>-</span>
                  </td>
                  <td class="text-center">{{ formatCurrency(collab.cout) }}</td>
                </tr>
                <tr v-if="topCollaborateurs.length === 0">
                  <td colspan="4" class="text-center">
                    Aucune donnée disponible
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Tableau des franchises -->
      <div class="col-md-6 mb-4">
        <div class="card h-100">
          <div class="card-header bg-purple text-white">
            <h5 class="mb-0">
              Analyse des franchises
            </h5>
          </div>
          <div class="card-body">
            <table class="table table-striped">
              <thead>
                <tr>
                  <th>Type de franchise</th>
                  <th class="text-center">Nombre</th>
                  <th class="text-center">% du total</th>
                  <th class="text-center">Coût moyen</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(stat, index) in franchiseStats" :key="index">
                  <td>{{ getFranchiseLabel(index) }}</td>
                  <td class="text-center">
                    <span class="badge badge-purple">{{ stat.count }}</span>
                  </td>
                  <td class="text-center">
                    {{ Math.round(stat.pourcentage * 100) / 100 }}%
                  </td>
                  <td class="text-center">
                    {{ formatCurrency(stat.coutMoyen) }}
                  </td>
                </tr>
                <tr v-if="franchiseStats.length === 0">
                  <td colspan="4" class="text-center">
                    Aucune donnée disponible
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { errHandler } from "@/helpers.js";
import BaseToolBar from "@/components/bases/ToolBar.vue";
import BaseButton from "@/components/bases/Button.vue";
import moment from "moment";

export default {
  components: {
    BaseToolBar,
    BaseButton
  },
  data() {
    return {
      sinistres: [],
      loading: false,

      // Filtres pour les derniers sinistres
      filtreEtat: "-1",
      filtreType: "-1",
      limiteSinistres: 10,
      // Définitions des types
      typesDommage: [
        { id: 0, description: "Carrosserie", color: "#36A2EB" },
        { id: 1, description: "Mécanique", color: "#FF6384" },
        { id: 2, description: "Bris de glace", color: "#4BC0C0" },
        { id: 3, description: "Vol", color: "#FF9F40" },
        { id: 4, description: "Incendie", color: "#9966FF" },
        { id: 5, description: "Autre", color: "#C9CBCF" }
      ],
      circonstancesDommage: [
        { id: 0, description: "Accident de la route", color: "#FF6384" },
        { id: 1, description: "Accident sur parking", color: "#36A2EB" },
        { id: 2, description: "Acte de vandalisme", color: "#FFCE56" },
        { id: 3, description: "Catastrophe naturelle", color: "#4BC0C0" },
        { id: 4, description: "Autre", color: "#9966FF" }
      ],
      etats: [
        { id: 0, description: "Déclaration en cours", color: "#FF9F40" },
        { id: 1, description: "En expertise", color: "#36A2EB" },
        { id: 2, description: "En réparation", color: "#FFCE56" },
        { id: 3, description: "Traité", color: "#4BC0C0" }
      ],
      optionsResponsabilite: [
        { id: 0, description: "0% (non responsable)", color: "#4BC0C0" },
        {
          id: 1,
          description: "50% (responsabilité partagée)",
          color: "#FFCE56"
        },
        { id: 2, description: "100% (responsable)", color: "#FF6384" },
        { id: 3, description: "Non déterminée", color: "#C9CBCF" }
      ],
      optionsFranchise: [
        { id: 0, description: "Pas de franchise" },
        { id: 1, description: "Franchise appliquée" },
        { id: 2, description: "Non déterminé" }
      ]
    };
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    fetchData() {
      this.loading = true;

      this.$http
        .get("/vehicules-sinistres")
        .then(response => {
          // Traiter la réponse des sinistres
          if (typeof response.data === "string") {
            console.warn(
              "Les données de sinistres reçues sont au format texte, pas au format JSON"
            );
            this.sinistres = [];
          } else {
            this.sinistres = response.data;
          }
        })
        .catch(error => {
          console.error(
            "Erreur lors du chargement des données:",
            errHandler(error)
          );
          this.$toast.error("Impossible de charger les données des sinistres");
          this.sinistres = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },

    // Méthode pour filtrer par état
    setFiltreEtat(etatId) {
      this.filtreEtat = etatId;
    },

    // Méthode pour voir tous les sinistres
    voirTousSinistres() {
      this.limiteSinistres = this.sinistresFiltres.length;
    },

    // Comptage des sinistres par état
    countSinistresByEtat(etatId) {
      return this.sinistres.filter(
        s => s.etat !== null && parseInt(s.etat) === etatId
      ).length;
    },

    // Coût total par état
    totalCoutByEtat(etatId) {
      return this.sinistres
        .filter(s => s.etat !== null && parseInt(s.etat) === etatId)
        .reduce((total, sinistre) => {
          return total + this.getSinistreCoutTotal(sinistre);
        }, 0);
    },

    getMonthlyData() {
      const monthlyData = {};
      const monthlyCoutSociete = {};
      const monthlyCoutAssurance = {};
      const now = moment();
      const sixMonthsAgo = moment().subtract(6, "months");

      // Initialiser les 6 derniers mois avec des compteurs à 0
      for (
        let m = moment(sixMonthsAgo);
        m.isSameOrBefore(now, "month");
        m.add(1, "month")
      ) {
        const monthKey = m.format("YYYY-MM");
        monthlyData[monthKey] = 0;
        monthlyCoutSociete[monthKey] = 0;
        monthlyCoutAssurance[monthKey] = 0;
      }

      // Remplir les données réelles
      this.sinistres.forEach(sinistre => {
        if (!sinistre.date_sinistre) return;

        const monthKey = moment(sinistre.date_sinistre).format("YYYY-MM");
        if (monthlyData[monthKey] !== undefined) {
          monthlyData[monthKey]++;
          monthlyCoutSociete[monthKey] +=
            parseFloat(sinistre.cout_societe) || 0;
          monthlyCoutAssurance[monthKey] +=
            parseFloat(sinistre.cout_assurance) || 0;
        }
      });

      // Formater pour ApexCharts
      const labels = Object.keys(monthlyData).map(monthKey =>
        moment(monthKey, "YYYY-MM").format("MMM YYYY")
      );

      return {
        labels,
        counts: Object.values(monthlyData),
        coutSociete: Object.values(monthlyCoutSociete),
        coutAssurance: Object.values(monthlyCoutAssurance)
      };
    },

    getCirconstanceData() {
      // Préparer les objets pour les données
      const coutSociete = [];
      const coutAssurance = [];
      const labels = [];

      // Vérifier si nous avons des données
      if (!this.sinistres || this.sinistres.length === 0) {
        return { labels: [], coutSociete: [], coutAssurance: [] };
      }

      // Parcourir les types de circonstances
      this.circonstancesDommage.forEach(circonstance => {
        // Filtrer les sinistres par cette circonstance
        const sinistresByCirconstance = this.sinistres.filter(
          s =>
            s.circonstance_dommage !== null &&
            s.circonstance_dommage !== undefined &&
            parseInt(s.circonstance_dommage) === circonstance.id
        );

        // Calculer les coûts totaux
        const totalCoutSociete = sinistresByCirconstance.reduce(
          (total, s) => total + (parseFloat(s.cout_societe) || 0),
          0
        );

        const totalCoutAssurance = sinistresByCirconstance.reduce(
          (total, s) => total + (parseFloat(s.cout_assurance) || 0),
          0
        );

        // Ajouter aux tableaux
        labels.push(circonstance.description);
        coutSociete.push(totalCoutSociete);
        coutAssurance.push(totalCoutAssurance);
      });

      // Si aucune donnée n'a été ajoutée, créer des données vides
      if (labels.length === 0) {
        this.circonstancesDommage.forEach(circonstance => {
          labels.push(circonstance.description);
          coutSociete.push(0);
          coutAssurance.push(0);
        });
      }

      return { labels, coutSociete, coutAssurance };
    },

    getQuarterlyData() {
      const quarters = {};
      const now = moment();
      const twoYearsAgo = moment().subtract(2, "years");

      // Initialiser les 8 derniers trimestres
      for (let q = moment(twoYearsAgo); q.isBefore(now); q.add(3, "months")) {
        const year = q.format("YYYY");
        const quarter = Math.ceil((q.month() + 1) / 3);
        const key = `${year}-Q${quarter}`;
        quarters[key] = {
          coutSociete: 0,
          coutAssurance: 0,
          count: 0
        };
      }

      // Remplir avec les données réelles
      this.sinistres.forEach(sinistre => {
        if (!sinistre.date_sinistre) return;

        const date = moment(sinistre.date_sinistre);
        const year = date.format("YYYY");
        const quarter = Math.ceil((date.month() + 1) / 3);
        const key = `${year}-Q${quarter}`;

        if (quarters[key]) {
          quarters[key].count++;
          quarters[key].coutSociete += parseFloat(sinistre.cout_societe) || 0;
          quarters[key].coutAssurance +=
            parseFloat(sinistre.cout_assurance) || 0;
        }
      });

      return {
        labels: Object.keys(quarters),
        coutSociete: Object.values(quarters).map(q => q.coutSociete),
        coutAssurance: Object.values(quarters).map(q => q.coutAssurance),
        counts: Object.values(quarters).map(q => q.count)
      };
    },

    getTypeDommageStats() {
      return this.typesDommage
        .map(type => {
          const count = this.sinistres.filter(
            s => s.type_dommage !== null && parseInt(s.type_dommage) === type.id
          ).length;

          return {
            id: type.id,
            count,
            label: type.description,
            color: type.color
          };
        })
        .filter(type => type.count > 0);
    },

    getResponsabiliteStats() {
      return this.optionsResponsabilite
        .map(resp => {
          const count = this.sinistres.filter(
            s =>
              s.responsabilite !== null &&
              parseInt(s.responsabilite) === resp.id
          ).length;

          return {
            id: resp.id,
            count,
            label: resp.description,
            color: resp.color
          };
        })
        .filter(resp => resp.count > 0);
    },

    exportStats() {
      this.loading = true;

      this.$http
        .get("/vehicules-sinistres-export", {
          responseType: "blob"
        })
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;

          const contentDisposition = response.headers["content-disposition"];
          let filename = "sinistres-export.xls";

          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="(.+)"/);
            if (filenameMatch && filenameMatch.length === 2) {
              filename = filenameMatch[1];
            }
          }

          link.setAttribute("download", filename);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          this.$toast.success("Export téléchargé avec succès");
        })
        .catch(error => {
          console.error("Erreur lors de l'export:", errHandler(error));
          this.$toast.error("Impossible d'exporter les données");
        })
        .finally(() => {
          this.loading = false;
        });
    },

    // Nouvelles méthodes helper pour les nouveaux champs
    getCollaborateurNom(collaborateurId) {
      if (!collaborateurId) return null;

      // Rechercher le sinistre qui contient l'ID collaborateur
      const sinistre = this.sinistres.find(
        s => s.collaborateur_id === collaborateurId
      );

      if (!sinistre) return null;

      // Construire le nom complet à partir des champs nom et prénom
      if (sinistre.collaborateur_nom || sinistre.collaborateur_prenom) {
        return `${sinistre.collaborateur_nom ||
          ""} ${sinistre.collaborateur_prenom || ""}`.trim();
      }

      return null;
    },

    getTypeDommageLabel(typeId) {
      if (typeId === null || typeId === undefined) return "Non défini";

      const type = this.typesDommage.find(t => t.id === parseInt(typeId));
      return type ? type.description : "Non défini";
    },

    getTypeDommageBadgeClass(typeId) {
      if (typeId === null || typeId === undefined) return "badge-secondary";

      const badges = [
        "badge-info", // Carrosserie
        "badge-danger", // Mécanique
        "badge-warning", // Bris de glace
        "badge-dark", // Vol
        "badge-danger", // Incendie
        "badge-secondary" // Autre
      ];

      return badges[parseInt(typeId)] || "badge-secondary";
    },

    getEtatLabel(etatId) {
      if (etatId === null || etatId === undefined) return "Non défini";

      const etat = this.etats.find(e => e.id === parseInt(etatId));
      return etat ? etat.description : "Non défini";
    },

    getEtatBadgeClass(etatId) {
      if (etatId === null || etatId === undefined) return "badge-secondary";

      const badges = [
        "badge-warning", // Déclaration en cours
        "badge-info", // En expertise
        "badge-primary", // En réparation
        "badge-success" // Traité
      ];

      return badges[parseInt(etatId)] || "badge-secondary";
    },

    getEtatClass(etatId) {
      const classes = [
        "warning", // Déclaration en cours
        "info", // En expertise
        "primary", // En réparation
        "success" // Traité
      ];

      return classes[etatId] || "secondary";
    },

    getFranchiseLabel(franchiseId) {
      if (franchiseId === null || franchiseId === undefined)
        return "Non défini";

      const franchise = this.optionsFranchise.find(
        f => f.id === parseInt(franchiseId)
      );
      return franchise ? franchise.description : "Non défini";
    },

    getResponsabilitePourcentage(score) {
      const pourcentages = ["0%", "50%", "100%", "?"];
      return pourcentages[score] || "?";
    },

    getSinistreCoutTotal(sinistre) {
      const coutSociete = parseFloat(sinistre.cout_societe) || 0;
      const coutAssurance = parseFloat(sinistre.cout_assurance) || 0;
      return coutSociete + coutAssurance;
    },

    formatCurrency(value) {
      if (value === undefined || value === null) return "0,00 €";
      return new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR"
      }).format(value);
    },

    formatDate(date) {
      if (!date) return "Non défini";
      return moment(date).format("DD/MM/YYYY");
    }
  },
  computed: {
    filtreEtatLabel() {
      if (this.filtreEtat === "-1") return null;
      const etat = this.etats.find(e => e.id === parseInt(this.filtreEtat));
      return etat ? etat.description : null;
    },

    sinistresFiltres() {
      let filtered = [...this.sinistres];

      // Filtrer par état si nécessaire
      if (this.filtreEtat !== "-1") {
        filtered = filtered.filter(
          s =>
            s.etat !== null &&
            s.etat !== undefined &&
            parseInt(s.etat) === parseInt(this.filtreEtat)
        );
      }

      // Filtrer par type si nécessaire
      if (this.filtreType !== "-1") {
        filtered = filtered.filter(
          s =>
            s.type_dommage !== null &&
            s.type_dommage !== undefined &&
            parseInt(s.type_dommage) === parseInt(this.filtreType)
        );
      }

      // Trier par date décroissante (plus récent d'abord)
      return filtered.sort((a, b) => {
        const dateA = new Date(a.date_sinistre || 0);
        const dateB = new Date(b.date_sinistre || 0);
        return dateB - dateA;
      });
    },

    derniersSinistresFiltres() {
      // Renvoyer le nombre limité de sinistres
      return this.sinistresFiltres.slice(0, this.limiteSinistres);
    },

    hasCirconstanceData() {
      const data = this.getCirconstanceData();
      return (
        data.coutSociete.some(val => val > 0) ||
        data.coutAssurance.some(val => val > 0)
      );
    },

    hasTypeDommageData() {
      return this.getTypeDommageStats().length > 0;
    },

    hasResponsabiliteData() {
      return this.getResponsabiliteStats().length > 0;
    },

    // Graphique circonstance
    circonstanceSeries() {
      const data = this.getCirconstanceData();
      return [
        {
          name: "Coût société",
          data: data.coutSociete
        },
        {
          name: "Coût assurance",
          data: data.coutAssurance
        }
      ];
    },

    circonstanceChartOptions() {
      const data = this.getCirconstanceData();
      return {
        chart: {
          type: "bar",
          height: 300,
          stacked: true,
          toolbar: {
            show: true
          },
          background: "transparent"
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "55%"
          }
        },
        xaxis: {
          categories: data.labels
        },
        fill: {
          opacity: 1
        },
        colors: ["#FF6384", "#36A2EB"],
        dataLabels: {
          enabled: false
        },
        yaxis: {
          title: {
            text: "Coût (€)"
          },
          labels: {
            formatter: function(val) {
              return new Intl.NumberFormat("fr-FR", {
                style: "currency",
                currency: "EUR",
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              }).format(val);
            }
          }
        },
        tooltip: {
          y: {
            formatter: function(val) {
              return new Intl.NumberFormat("fr-FR", {
                style: "currency",
                currency: "EUR"
              }).format(val);
            }
          }
        },
        legend: {
          position: "top"
        },
        theme: {
          mode:
            this.$store && this.$store.getters["colors/darkMode"]
              ? "dark"
              : "light"
        }
      };
    },

    // Graphique trimestriel
    quarterlySeries() {
      const data = this.getQuarterlyData();
      return [
        {
          name: "Coût société",
          data: data.coutSociete
        },
        {
          name: "Coût assurance",
          data: data.coutAssurance
        }
      ];
    },

    quarterlyChartOptions() {
      const data = this.getQuarterlyData();
      return {
        chart: {
          type: "bar",
          height: 300,
          stacked: true,
          toolbar: {
            show: true
          },
          background: "transparent"
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "55%",
            endingShape: "rounded"
          }
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          show: true,
          width: 2,
          colors: ["transparent"]
        },
        xaxis: {
          categories: data.labels
        },
        yaxis: {
          title: {
            text: "Coût (€)"
          },
          labels: {
            formatter: function(val) {
              return new Intl.NumberFormat("fr-FR", {
                style: "currency",
                currency: "EUR",
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              }).format(val);
            }
          }
        },
        fill: {
          opacity: 1
        },
        tooltip: {
          y: {
            formatter: function(val) {
              return new Intl.NumberFormat("fr-FR", {
                style: "currency",
                currency: "EUR"
              }).format(val);
            }
          }
        },
        colors: ["#FF6384", "#36A2EB"],
        legend: {
          position: "bottom"
        },
        theme: {
          mode:
            this.$store && this.$store.getters["colors/darkMode"]
              ? "dark"
              : "light"
        }
      };
    },

    // Graphique mensuel
    monthlySeries() {
      const monthlyData = this.getMonthlyData();
      return [
        {
          name: "Nombre de sinistres",
          type: "column",
          data: monthlyData.counts
        },
        {
          name: "Coût société",
          type: "line",
          data: monthlyData.coutSociete
        },
        {
          name: "Coût assurance",
          type: "line",
          data: monthlyData.coutAssurance
        }
      ];
    },

    monthlyChartOptions() {
      const monthlyData = this.getMonthlyData();
      return {
        chart: {
          type: "line",
          height: 300,
          toolbar: {
            show: true
          },
          animations: {
            enabled: true,
            easing: "easeinout",
            speed: 800
          },
          background: "transparent"
        },
        stroke: {
          curve: "smooth",
          width: [3, 3, 3]
        },
        colors: ["#36A2EB", "#FF6384", "#4BC0C0"],
        fill: {
          type: ["solid", "solid", "solid"],
          opacity: [0.7, 0.3, 0.3]
        },
        labels: monthlyData.labels,
        xaxis: {
          type: "category",
          labels: {
            rotateAlways: false
          }
        },
        yaxis: [
          {
            title: {
              text: "Nombre de sinistres"
            },
            labels: {
              formatter: function(val) {
                return val.toFixed(0);
              }
            }
          },
          {
            opposite: true,
            title: {
              text: "Coût (€)"
            },
            labels: {
              formatter: function(val) {
                return new Intl.NumberFormat("fr-FR", {
                  style: "currency",
                  currency: "EUR",
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                }).format(val);
              }
            }
          }
        ],
        dataLabels: {
          enabled: false
        },
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: function(value, { seriesIndex }) {
              if (seriesIndex === 0) {
                return value.toFixed(0) + " sinistres";
              } else {
                return new Intl.NumberFormat("fr-FR", {
                  style: "currency",
                  currency: "EUR"
                }).format(value);
              }
            }
          }
        },
        legend: {
          position: "top"
        },
        grid: {
          borderColor: "#e0e0e0",
          strokeDashArray: 5
        },
        theme: {
          mode:
            this.$store && this.$store.getters["colors/darkMode"]
              ? "dark"
              : "light"
        }
      };
    },

    // Graphique types de dommages
    typeDommageSeries() {
      const stats = this.getTypeDommageStats();
      return stats.map(t => t.count);
    },

    typeDommageChartOptions() {
      const stats = this.getTypeDommageStats();
      return {
        chart: {
          type: "donut",
          height: 180,
          background: "transparent"
        },
        labels: stats.map(t => t.label),
        colors: stats.map(t => t.color),
        legend: {
          position: "bottom",
          fontSize: "12px",
          fontFamily: "Helvetica, Arial, sans-serif",
          offsetY: 0
        },
        dataLabels: {
          enabled: false
        },
        tooltip: {
          y: {
            formatter: function(val) {
              return val + " sinistre(s)";
            }
          }
        },
        plotOptions: {
          pie: {
            donut: {
              size: "65%",
              background: "transparent"
            }
          }
        },
        theme: {
          mode:
            this.$store && this.$store.getters["colors/darkMode"]
              ? "dark"
              : "light"
        }
      };
    },

    // Graphique responsabilités
    responsabiliteSeries() {
      const stats = this.getResponsabiliteStats();
      return stats.map(r => r.count);
    },

    responsabiliteChartOptions() {
      const stats = this.getResponsabiliteStats();
      return {
        chart: {
          type: "donut",
          height: 180,
          background: "transparent"
        },
        labels: stats.map(r => r.label),
        colors: stats.map(r => r.color),
        legend: {
          position: "bottom",
          fontSize: "12px",
          fontFamily: "Helvetica, Arial, sans-serif",
          offsetY: 0
        },
        dataLabels: {
          enabled: false
        },
        tooltip: {
          y: {
            formatter: function(val) {
              return val + " sinistre(s)";
            }
          }
        },
        plotOptions: {
          pie: {
            donut: {
              size: "65%",
              background: "transparent"
            }
          }
        },
        theme: {
          mode:
            this.$store && this.$store.getters["colors/darkMode"]
              ? "dark"
              : "light"
        }
      };
    },

    coutTotalSociete() {
      return this.sinistres.reduce((total, sinistre) => {
        return total + (parseFloat(sinistre.cout_societe) || 0);
      }, 0);
    },

    coutTotalAssurance() {
      return this.sinistres.reduce((total, sinistre) => {
        return total + (parseFloat(sinistre.cout_assurance) || 0);
      }, 0);
    },

    coutTotal() {
      return this.coutTotalSociete + this.coutTotalAssurance;
    },

    coutMoyenSinistre() {
      if (this.sinistres.length === 0) return 0;
      return this.coutTotal / this.sinistres.length;
    },

    // Statistiques des états
    etatsStatistiques() {
      const etatsStats = [0, 0, 0, 0]; // Compteurs pour chaque état
      let total = 0;

      // Compter les sinistres par état
      this.sinistres.forEach(sinistre => {
        if (sinistre.etat !== null && sinistre.etat !== undefined) {
          const etatId = parseInt(sinistre.etat);
          if (etatId >= 0 && etatId < 4) {
            etatsStats[etatId]++;
            total++;
          }
        }
      });

      // Calculer les pourcentages et formater les résultats
      return this.etats.map((etat, index) => {
        const count = etatsStats[index];
        const pourcentage = total > 0 ? (count / total) * 100 : 0;

        return {
          id: etat.id,
          label: etat.description,
          count,
          pourcentage: Math.round(pourcentage * 10) / 10
        };
      });
    },

    // Statistiques des franchises
    franchiseStats() {
      const stats = [];

      // Pour chaque type de franchise
      for (let i = 0; i < this.optionsFranchise.length; i++) {
        // Filtrer les sinistres avec ce type de franchise
        const sinistresFranchise = this.sinistres.filter(
          s => s.franchise !== null && parseInt(s.franchise) === i
        );

        // Calculer le nombre, le pourcentage et le coût moyen
        const count = sinistresFranchise.length;
        const pourcentage =
          this.sinistres.length > 0 ? (count / this.sinistres.length) * 100 : 0;

        // Calculer le coût moyen
        let coutTotal = 0;
        sinistresFranchise.forEach(sinistre => {
          coutTotal += this.getSinistreCoutTotal(sinistre);
        });

        const coutMoyen = count > 0 ? coutTotal / count : 0;

        stats.push({
          count,
          pourcentage,
          coutMoyen
        });
      }

      return stats;
    },

    topVehiculesSinistres() {
      // Regrouper par véhicule
      const vehiculesMap = {};

      this.sinistres.forEach(sinistre => {
        if (!sinistre.vehicule_id) return;

        if (!vehiculesMap[sinistre.vehicule_id]) {
          vehiculesMap[sinistre.vehicule_id] = {
            vehicule_id: sinistre.vehicule_id,
            immatriculation: sinistre.immatriculation || "Non défini",
            modele: sinistre.modele || "Non défini",
            nbSinistres: 0,
            coutSociete: 0,
            coutAssurance: 0
          };
        }

        vehiculesMap[sinistre.vehicule_id].nbSinistres++;
        vehiculesMap[sinistre.vehicule_id].coutSociete +=
          parseFloat(sinistre.cout_societe) || 0;
        vehiculesMap[sinistre.vehicule_id].coutAssurance +=
          parseFloat(sinistre.cout_assurance) || 0;
      });

      // Calculer le coût total pour chaque véhicule
      Object.values(vehiculesMap).forEach(vehicule => {
        vehicule.coutTotal = vehicule.coutSociete + vehicule.coutAssurance;
      });

      // Convertir en tableau et trier par nombre de sinistres
      return Object.values(vehiculesMap)
        .sort((a, b) => b.nbSinistres - a.nbSinistres)
        .slice(0, 10);
    },

    // Top 5 des collaborateurs impliqués dans des sinistres
    topCollaborateurs() {
      // Regrouper par collaborateur
      const collabMap = {};

      // Ajouter une entrée pour les sinistres sans collaborateur attribué
      collabMap["non_attribue"] = {
        id: null,
        nom: null,
        count: 0,
        cout: 0,
        responsabiliteScore: 0
      };

      this.sinistres.forEach(sinistre => {
        if (!sinistre.collaborateur_id) {
          // Cas des sinistres sans collaborateur
          collabMap["non_attribue"].count++;
          collabMap["non_attribue"].cout += this.getSinistreCoutTotal(sinistre);
          return;
        }

        const collabId = sinistre.collaborateur_id;

        if (!collabMap[collabId]) {
          // Construire le nom du collaborateur à partir des champs
          const nom = `${sinistre.collaborateur_nom ||
            ""} ${sinistre.collaborateur_prenom || ""}`.trim();

          collabMap[collabId] = {
            id: collabId,
            nom: nom || `Collaborateur #${collabId}`,
            count: 0,
            cout: 0,
            responsabiliteTotal: 0
          };
        }

        collabMap[collabId].count++;
        collabMap[collabId].cout += this.getSinistreCoutTotal(sinistre);

        // Ajouter la responsabilité pour calculer la moyenne plus tard
        if (
          sinistre.responsabilite !== null &&
          sinistre.responsabilite !== undefined
        ) {
          collabMap[collabId].responsabiliteTotal += parseInt(
            sinistre.responsabilite
          );
        }
      });

      // Calculer le score moyen de responsabilité
      Object.values(collabMap).forEach(collab => {
        if (collab.count > 0) {
          collab.responsabiliteScore = Math.round(
            collab.responsabiliteTotal / collab.count
          );
        }
      });

      // Convertir en tableau et trier par nombre de sinistres
      return Object.values(collabMap)
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);
    },

    derniersSinistres() {
      // Filtrer les sinistres qui ont une date
      const sinistresAvecDate = this.sinistres.filter(s => s.date_sinistre);

      // Trier par date décroissante (plus récent d'abord)
      return [...sinistresAvecDate]
        .sort((a, b) => {
          const dateA = new Date(a.date_sinistre);
          const dateB = new Date(b.date_sinistre);
          return dateB - dateA;
        })
        .slice(0, 10); // Prendre les 10 premiers résultats
    }
  }
};
</script>

<style lang="css">
.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  transition: all 0.3s ease;
}

.card:hover {
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.card-header {
  font-weight: 500;
}

.table th {
  background-color: #f8f9fa;
}

.progress {
  border-radius: 0.5rem;
  overflow: hidden;
  height: 20px;
}

.progress-stacked {
  display: flex;
  height: 20px;
  border-radius: 0.5rem;
  overflow: hidden;
}

.badge {
  padding: 0.35em 0.65em;
  font-size: 0.75em;
}

.badge-danger {
  background-color: #dc3545;
  color: white;
}

.badge-warning {
  background-color: #ffc107;
  color: #212529;
}

.badge-info {
  background-color: #17a2b8;
  color: white;
}

.badge-success {
  background-color: #28a745;
  color: white;
}

.badge-primary {
  background-color: #007bff;
  color: white;
}

.badge-purple {
  background-color: #6f42c1;
  color: white;
}

.badge-dark {
  background-color: #343a40;
  color: white;
}

.bg-success {
  background-color: #28a745 !important;
}

.bg-warning {
  background-color: #ffc107 !important;
}

.bg-info {
  background-color: #17a2b8 !important;
}

.bg-primary {
  background-color: #007bff !important;
}

.bg-purple {
  background-color: #6f42c1 !important;
  color: white !important;
}

.border-primary {
  border-top: 4px solid #007bff !important;
}

.border-info {
  border-top: 4px solid #17a2b8 !important;
}

.border-success {
  border-top: 4px solid #28a745 !important;
}

.border-warning {
  border-top: 4px solid #ffc107 !important;
}

.border-danger {
  border-top: 4px solid #dc3545 !important;
}

.border-purple {
  border-top: 4px solid #6f42c1 !important;
}

.chart-container {
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 8px;
}

/* Nouveaux styles pour les cartes d'état */
.card-etat {
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 0.2s ease;
}

.card-etat:hover {
  transform: translateY(-5px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.card-etat-active {
  border-color: #333;
  transform: translateY(-5px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.2);
}

/* Classes de couleur pour les états */
.bg-warning-light {
  background-color: rgba(255, 193, 7, 0.2);
}

.bg-info-light {
  background-color: rgba(23, 162, 184, 0.2);
}

.bg-primary-light {
  background-color: rgba(0, 123, 255, 0.2);
}

.bg-success-light {
  background-color: rgba(40, 167, 69, 0.2);
}

.bg-danger-light {
  background-color: rgba(220, 53, 69, 0.2);
}
.col-md-2-4 {
  position: relative;
  width: 100%;
  padding-right: 15px;
  padding-left: 15px;
}

@media (min-width: 768px) {
  .col-md-2-4 {
    flex: 0 0 20%;
    max-width: 20%;
  }
}
</style>
